import { PAYMENT_ACTIONS, POPUP_IDS } from './constants.js';
import { storePayment } from './store.js';
import { finalizeSessionPayment } from './ajax.js';

/**
 * Get or create a Stripe instance (singleton)
 * Uses publishKey from checkout form or wpcore global
 */
let _stripeInstance = null;
export function getStripeInstance() {
	if (_stripeInstance) return _stripeInstance;

	const publishKey =
		document.querySelector('input[name="publish_key"]')?.value ||
		window.wpcore?.stripePublishKey;

	if (!publishKey) {
		console.error('Stripe publish key not found');
		return null;
	}

	const locale = window.wpcore?.direct_t?.({ en: 'en', fr: 'fr' }) || 'en';
	_stripeInstance = Stripe(publishKey, { locale });
	return _stripeInstance;
}

/**
 * Handle payment response from AJAX.
 *
 * Possible response shapes:
 *   { status: 'active'|'trialing', ... }                     → success
 *   { status: 'incomplete', requires_action: true, client_secret: '...' } → needs 3DS / card confirm
 *   null                                                      → network error / server 4xx
 */
export async function displayPopup(response, btn, action) {
	if (!response) {
		resetButton(btn);
		wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
		return;
	}

	// Handle error response (server returned error, e.g. duplicate subscription or card declined)
	if (response.error) {
		resetButton(btn);
		const failMsg = document.getElementById('payment-fail-message');
		if (failMsg) {
			failMsg.textContent = response.error;
		}
		wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
		return;
	}

	const SUCCESS_STATUSES = ['active', 'trialing', 'succeeded'];

	// Payment needs frontend confirmation (3DS or card auth)
	if (response.requires_action && response.client_secret) {
		btn.innerText = wpcore.direct_t({ en: 'Authenticating...', fr: 'Authentification...' });

		const stripe = getStripeInstance();
		if (!stripe) {
			resetButton(btn);
			wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
			return;
		}

		// Trial + 3DS: confirm SetupIntent, then retry subscription creation
		if (response.requires_3ds_setup) {
			const { error, setupIntent } = await stripe.confirmCardSetup(response.client_secret);

			if (error) {
				console.error('3DS setup confirmation failed:', error.message);
				resetButton(btn);
				wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
				return;
			}

			if (setupIntent && setupIntent.status === 'succeeded') {
				// 3DS confirmed — now create subscription (skip verification on retry)
				btn.innerText = wpcore.direct_t({ en: 'Creating subscription...', fr: 'Création de l\'abonnement...' });
				const { createSubscriptionAfter3DS } = await import('./ajax.js');
				const nonce = btn.dataset.nonce || document.querySelector('input[name="nonce"]')?.value;
				const subId = response.subscription_id_wp || btn.dataset.subscriptionId;
				const pmId = response.payment_method_id || storePayment.getPaymentMethod();
				const retryResponse = await createSubscriptionAfter3DS(pmId, subId, nonce);
				await displayPopup(retryResponse, btn, action);
				return;
			}

			resetButton(btn);
			wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
			return;
		}

		// Non-trial: confirm PaymentIntent (subscription charge)
		const { error, paymentIntent } = await stripe.confirmCardPayment(response.client_secret);

		if (error) {
			console.error('Card confirmation failed:', error.message);
			resetButton(btn);
			wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
			return;
		}

		// PaymentIntent succeeded after 3DS
		if (paymentIntent && paymentIntent.status === 'succeeded') {
			btn.innerText = wpcore.direct_t({ en: 'Payment succeed', fr: 'Paiement réussi' });
			if (action === PAYMENT_ACTIONS.PAYMENT_SESSION && paymentIntent.id) {
				const nonce = btn.dataset.nonce || document.querySelector('input[name="nonce"]')?.value;
				const finalized = await finalizeSessionPayment(nonce, paymentIntent.id);
				if (!finalized || finalized.completed === false) {
					resetButton(btn);
					wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
					return;
				}
			}
			showSuccessPopup(action, { amount_charged: paymentIntent.amount, currency: paymentIntent.currency });
			return;
		}

		// Unexpected PI status
		resetButton(btn);
		wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
		return;
	}

	// Payment fully succeeded server-side (no 3DS needed)
	if (SUCCESS_STATUSES.includes(response.status)) {
		btn.innerText = wpcore.direct_t({ en: 'Payment succeed', fr: 'Paiement réussi' });
		showSuccessPopup(action, response);
		return;
	}

	// Subscription incomplete but no client_secret → card likely declined
	resetButton(btn);
	wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
}

/**
 * Show the success popup for the given payment action.
 */
function showSuccessPopup(action, response) {
	if (action === PAYMENT_ACTIONS.UPGRADE) {
		wpcore.showPopup(POPUP_IDS.UPGRADE_SUCCESS);
	} else if (action === PAYMENT_ACTIONS.PAYMENT_SESSION) {
		wpcore.showPopup(POPUP_IDS.PAYMENT_SESSION_SUCCESS);
	} else if (action === PAYMENT_ACTIONS.PAYMENT_SUBSCRIPTION) {
		// Show real charged amount (post-discount). amount_charged is in cents.
		const amountCharged = response?.amount_charged;
		if (amountCharged && amountCharged > 0) {
			const popupAmount = document.getElementById('popup-amount');
			if (popupAmount) {
				const currency = (response?.currency || 'eur').toUpperCase();
				const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency + ' ';
				popupAmount.textContent = symbol + (amountCharged / 100).toFixed(2);
			}
		}
		wpcore.showPopup(POPUP_IDS.PAYMENT_SUBSCRIPTION_SUCCESS);
	}
}

/**
 * Reset button state after failure
 */
function resetButton(btn) {
	btn.disabled = false;
	btn.innerText = wpcore.direct_t({ en: 'Confirm and pay', fr: 'Confirmer et payer' });
}

/**
 * Create PaymentMethod from Stripe Elements.
 *
 * Uses createPaymentMethod (not confirmSetup) so it can be called multiple times
 * even after a failed payment attempt — SetupIntent can only be confirmed once,
 * but createPaymentMethod works independently.
 *
 * 3DS authentication is handled separately:
 * - Trial subscriptions: backend creates SetupIntent → frontend confirms via confirmCardSetup
 * - Non-trial subscriptions: subscription charge PI → frontend confirms via confirmCardPayment
 */
export async function confirmSetup(stripe, elements, submitBtn) {
	// Clear any previously stored PM to prevent stale reuse on retry
	storePayment.clearPaymentMethod();

	// Validate form fields
	const { error: submitError } = await elements.submit();
	if (submitError) {
		console.error('elements.submit failed:', submitError.message);
		submitBtn.disabled = false;
		submitBtn.innerText = wpcore.direct_t({ en: 'Confirm and Pay', fr: 'Confirmer et payer' });
		wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
		return;
	}

	// Create PaymentMethod without confirming SetupIntent — works on retry
	const { error, paymentMethod } = await stripe.createPaymentMethod({ elements });

	if (error || !paymentMethod) {
		console.error('createPaymentMethod failed:', error?.message);
		submitBtn.disabled = false;
		submitBtn.innerText = wpcore.direct_t({ en: 'Confirm and Pay', fr: 'Confirmer et payer' });
		wpcore.showPopup(POPUP_IDS.PAYMENT_FAIL);
		return;
	}

	storePayment.setPaymentMethod(paymentMethod.id);
}
